import { useMutation } from "@apollo/client";
import axios from "axios";
import {
  AdminApprovalQuoteQuery,
  removeAdminAttachmentMutation,
} from "generated/graphql";
import * as React from "react";
import { useDropzone } from "react-dropzone";
import { UploadedFileList } from "./LcpWorkflow/FileClaimPage";

const maxMbSize = 5;

export function AdminAttachmentControl({
  appId,
  attachments,
  refetch,
}: {
  appId: number;
  attachments: AdminApprovalQuoteQuery["adminAttachments"] | undefined;
  refetch: () => void;
}) {
  const [mutateRemove] = useMutation(removeAdminAttachmentMutation);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async (acceptedFiles: File[]) => {
      await uploadFiles({ files: acceptedFiles, appId });
      refetch();
    },
    maxSize: maxMbSize * 1024 * 1024,
    accept: [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      // for .eml - this is type that Dropzone reports
      "message/rfc822",
    ],
    onDropRejected: rejections => {
      // Just show one message, even if multiple errors
      const baseMessage = rejections[0].errors[0].message;
      const message = baseMessage.includes("File type must be")
        ? "This file type is not supported."
        : baseMessage.includes("File is larger")
        ? "This file is too large."
        : baseMessage;
      window.alert(message);
    },
  });
  return (
    <>
      <UploadedFileList
        files={(attachments ?? []).map(a => ({
          filename: a.filename,
          attachmentId: a.id.toString(),
          onRemove: async () => {
            if (!window.confirm(`Remove ${a.filename}?`)) {
              return;
            }
            await mutateRemove({
              variables: {
                input: { attachmentId: a.id },
              },
            });
            refetch();
          },
        }))}
      />
      <div
        {...getRootProps()}
        className="drop-box"
        style={{
          position: "relative",
          borderWidth: "2px",
          borderColor: isDragActive ? "#5e0013" /* primary */ : "#ddd",
          borderStyle: "dashed",
          borderRadius: "15px",
          margin: "20px auto",
          padding: "25px",
          paddingTop: "15px",
          backgroundColor: "#F8F8F8",
          cursor: "pointer",
        }}
      >
        <input {...getInputProps()} className="file-input" />
        <p style={{ textAlign: "center" }}>
          <i className="fa fa-upload" />
          <br />
          <strong>(Optional) Click or drag here to attach files</strong>
          <br />
          (PDF, .doc, .docx, .xls, .xlsx, .csv or .eml format)
          <br />
          Maximum file size for each upload: {maxMbSize} MB
        </p>
      </div>
    </>
  );
}

async function uploadFiles({ files, appId }: { files: File[]; appId: number }) {
  const submitUrl = `/api/adminAttachment/${appId}`;
  const fd = new FormData();
  files.forEach(f => fd.append("files", f));
  await axios.put(submitUrl, fd, {
    headers: {
      // Header for multple files: https://stackoverflow.com/questions/58381990/react-axios-multiple-files-upload
      "Content-Type": "multipart/form-data",
    },
  });
}
